import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline'
import { GET_DISCOUNT_RULES } from '../../graphql/discount-queries'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import LoadingSpinner from '../../components/LoadingSpinner'
import type { DiscountRule, discount_class, trigger_type } from '../../types/discount'

function formatEffect(rule: DiscountRule): string {
  if (rule.effect_type === 'PERCENTAGE') {
    return `${rule.effect_value / 100}%`
  }
  if (rule.effect_type === 'MULTI_BUY') {
    return `Any ${rule.multi_buy_qty ?? '?'} for €${(rule.effect_value / 100).toFixed(2)}`
  }
  return `€${(rule.effect_value / 100).toFixed(2)}`
}

const CLASS_LABELS: Record<discount_class, string> = {
  ORDER: 'Order',
  PRODUCT: 'Product',
}

const PAGE_META: Record<trigger_type, { title: string; description: string; empty: string; newHref: string }> = {
  AUTOMATIC: {
    title: 'Automatic Discounts',
    description: 'Apply without any action — great for happy hours and lunch deals',
    empty: 'No automatic discounts yet. Create one to apply discounts without any code or staff action.',
    newHref: '/discounts/new/automatic',
  },
  CODE: {
    title: 'Coupon Codes',
    description: 'Activated when a customer or staff member enters a code at checkout',
    empty: 'No coupon code discounts yet. Create one and share codes with customers.',
    newHref: '/discounts/new/coupon',
  },
  STAFF_APPLIED: {
    title: 'Staff Discounts',
    description: 'Applied manually by a staff member — useful for overrides and goodwill gestures',
    empty: 'No staff-applied discounts yet. Create one for manager overrides or loyalty rewards.',
    newHref: '/discounts/new/staff',
  },
}

type ActiveFilter = 'all' | 'active' | 'inactive'
type ClassFilter = 'all' | discount_class

interface Props {
  triggerFilter: trigger_type
}

export default function DiscountsPage({ triggerFilter }: Props) {
  const { hasPermission } = usePermissions()
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [classFilter, setClassFilter] = useState<ClassFilter>('all')

  const variables: Record<string, unknown> = { trigger_type: triggerFilter }
  if (activeFilter === 'active') variables.active = true
  if (activeFilter === 'inactive') variables.active = false
  if (classFilter !== 'all') variables.discount_class = classFilter

  const { data, loading, error } = useQuery(GET_DISCOUNT_RULES, { variables })
  const rules: DiscountRule[] = data?.discountRules ?? []

  const meta = PAGE_META[triggerFilter]

  return (
    <div className="space-y-6">
      {triggerFilter === 'STAFF_APPLIED' && (
        <div className="card p-4 bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <strong>Staff-applied discounts are not yet active in the POS checkout.</strong> You can create and manage rules here, but cashiers cannot apply them during a sale yet. This feature is coming soon.
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{meta.title}</h1>
          <p className="text-sm text-gray-500 mt-1">{meta.description}</p>
        </div>
        {hasPermission(PERMISSIONS.DISCOUNT_MANAGE) && (
          <Link to={meta.newHref} className="btn-primary flex items-center gap-2">
            <PlusIcon className="h-4 w-4" />
            New Discount
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <div className="flex rounded-md shadow-sm">
            {(['all', 'active', 'inactive'] as ActiveFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-3 py-1.5 text-sm border first:rounded-l-md last:rounded-r-md capitalize ${
                  activeFilter === f
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Applies to</label>
          <div className="flex rounded-md shadow-sm">
            {(['all', 'ORDER', 'PRODUCT'] as ClassFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setClassFilter(f)}
                className={`px-3 py-1.5 text-sm border first:rounded-l-md last:rounded-r-md ${
                  classFilter === f
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {f === 'all' ? 'All' : CLASS_LABELS[f]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <div className="card p-6 text-center text-red-600">
          Failed to load discount rules. Please try again.
        </div>
      )}

      {!loading && !error && rules.length === 0 && (
        <div className="card p-12 text-center">
          <div className="text-4xl mb-3">🏷️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Nothing here yet</h3>
          <p className="text-gray-500 mb-4">{meta.empty}</p>
          {hasPermission(PERMISSIONS.DISCOUNT_MANAGE) && (
            <Link to={meta.newHref} className="btn-primary inline-flex items-center gap-2">
              <PlusIcon className="h-4 w-4" />
              New Discount
            </Link>
          )}
        </div>
      )}

      {!loading && !error && rules.length > 0 && (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applies to</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{rule.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{CLASS_LABELS[rule.discount_class]}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 font-mono">{formatEffect(rule)}</td>
                  <td className="px-6 py-4">
                    {rule.active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    <Link to={`/discounts/${rule.id}`} className="text-indigo-600 hover:text-indigo-900 inline-flex items-center gap-1">
                      <EyeIcon className="h-4 w-4" />
                      View
                    </Link>
                    {hasPermission(PERMISSIONS.DISCOUNT_MANAGE) && (
                      <Link to={`/discounts/${rule.id}/edit`} className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
